import { Inquiry } from './../../../models/inquiry.model';
import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../material.module';
import { Router, RouterModule } from '@angular/router';
import { CellClickedEvent, ColDef, ColGroupDef } from 'ag-grid-community';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { blankOpen, escapeHTML, formatAmount, formatDate } from '../../../misc/functions';
import { FormsModule } from '@angular/forms';
import { ModalSnackbarService } from '../../../services/modal-snackbar.service';
import { LabelChipRendererComponent } from '../../label-chip/label-chip-renderer.componemt';
import { AG_GRID_LOCALE_JP } from '../../../misc/ag-grid-locale';
import { CustomSelectFilterComponent, CustomTextFilterComponent } from '../../../misc/custom-filter.component';
import { AdminUser, AdminUsersService } from '../../../services/admin-users.service';
import { WithdrawalService } from '../../../services/withdrawal.service';
import { Withdrawal } from '../../../models/withdrawal.model';
import { WithdrawalTransactionService } from '../../../services/withdrawal-transaction.service';
import { WithdrawalTransaction } from '../../../models/withdrawal-transaction.model';
import { AdminWithdrawalTransactionCreateDialogComponent } from './admin-withdrawal-transaction-create.dialog.component/admin-withdrawal-transaction-create.dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { bccomp } from '../../../misc/bcmath';

@Component({
  selector: 'app-admin-withdrawal-transaction-list',
  standalone: true,
  imports: [CommonModule, MaterialModule, RouterModule, AgGridModule, FormsModule, LabelChipRendererComponent],
  templateUrl: './admin-withdrawal-transaction-list.component.html',
  styleUrls: ['./admin-withdrawal-transaction-list.component.scss']
})
export class AdminWithdrawalTransactionListComponent implements OnInit {

  localeText = AG_GRID_LOCALE_JP;
  formatAmount = formatAmount;

  defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    suppressHeaderMenuButton: true,
    suppressFloatingFilterButton: true,
    suppressFiltersToolPanel: true,
    floatingFilterComponent: CustomTextFilterComponent
  };

  columnDefs: ColDef[] = [
    { field: 'id', headerName: 'ID', width: 105, sortable: true},
    { field: 'created_at', headerName: '出金額確定日時(登録日時)', width: 180, valueFormatter: params => formatDate(params.value), sortable: true},
    { field: 'user_id', headerName: 'ユーザー', width: 250, sortable: true,
      cellRenderer: (params: any) => params.data.name ? `${params.data.name} (${params.data.login})` : params.data.login,
      filterValueGetter: (params: any) => params.data.name ? `${params.data.name} (${params.data.login})` : params.data.login
    },
    { field: 'inquiries_id', headerName: '出金依頼ID\n(クリックで出金依頼詳細)', width: 200, wrapText: true, autoHeight: true, sortable: true,
      cellRenderer: (params: any) => !params.value ? 'なし（単独作成）' : `<button style="width: 100%;"><span title="${escapeHTML(params.data.inquiry.first_child.body)}" style="display: block; width: 100%; height: 100%; cursor: help;">📄&nbsp;${params.value}</span></button>`
    },
    { field: 'currency', headerName: '通貨', width: 100, wrapText: true, autoHeight: true, sortable: true, cellStyle: {'text-align': 'center'}},
    { field: 'requested_amount', headerName: '出金確定額', flex: 1, wrapText: true, autoHeight: true, sortable: true,
      cellRenderer: (params: any) => this.formatAmount(params.value, params.data.currency), cellStyle: {'text-align': 'right'}
    },
    { field: 'settled_amount', headerName: '送金済', flex: 1, wrapText: true, autoHeight: true, sortable: true,
      cellRenderer: (params: any) => this.formatAmount(params.value, params.data.currency), cellStyle: {'text-align': 'right'}
    },
    { field: 'unpaid_amount', headerName: '未送金', flex: 1, wrapText: true, autoHeight: true, sortable: true,
      cellRenderer: (params: any) => this.formatAmount(params.value, params.data.currency), cellStyle: {'text-align': 'right'}
    },
  ];

  paginationPageSizeSelector: number[] | boolean = [10, 20, 50, 100];

  showCompleted = false; // デフォルトは「完了は非表示」
  showOnlyUnassigned = false; // デフォルトは「全て表示」

  allRows: WithdrawalTransaction[] = []; // APIレスポンスを保持
  rowData: WithdrawalTransaction[] = []; // 表示用データ

  adminUsers: AdminUser[] = [];
  showGrid = false;
  isLoaded = false;

  @ViewChild('grid') grid!: AgGridAngular;

  constructor(
    private service: WithdrawalTransactionService,
    private snackBar: ModalSnackbarService,
    private router: Router,
    private adminUsersService: AdminUsersService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.load();

    this.adminUsersService.getAdminUsers().subscribe(res => {
      this.adminUsers = this.adminUsersService.parseData(res);
      this.isLoaded = true;

      const col: ColDef|undefined = this.columnDefs.find((x: ColDef) => x.field === 'admin_user_id');
      if (col) {
        col.floatingFilterComponentParams = {
          filterType: 'equals',
          options: this.adminUsers.map(x => {
            return {label: x.user_name, value: x.id};
          })
        }

        if (this.grid) {
          this.grid.api.setGridOption("columnDefs", this.columnDefs);
        }
      }

      this.showGrid = true;
    });
  }

  applyFilter(): void {
    this.rowData = this.allRows.filter(row => {
      // 「送金済・完了・単独未送金０」フィルタ：false のときに「送金済・完了・単独未送金０」を除外
      if (!this.showCompleted) {
        if (row.inquiry && (row.inquiry.status === '完了' || row.inquiry.status === '出金済')) {
          return false;
        }
        else if (bccomp(row.unpaid_amount, '0') === 0) {
          return false;
        }
      }

      return true;
    });
  }

  onToggleOnlyAssigned(): void {
    this.applyFilter();
  }

  onToggleCompleted(): void {
    this.applyFilter();
  }

  load(): void {
    this.service.getAll(true).subscribe({
      next: (res) => {
        this.allRows = res;
        this.applyFilter();
      },
      error: () => this.snackBar.show('出金依頼一覧の取得に失敗しました')
    });
  }

  onCellClicked(event: CellClickedEvent) {
    if (event.colDef.field === 'inquiries_id' && event.data.inquiries_id) {
      blankOpen(undefined, '/client/admin/inquiry/' + event.data.inquiries_id);
    }
    else {
      this.router.navigate(['/admin/withdrawalTransaction', event.data.id]);
    }
  }

  newTransaction() {

    const dialogRef = this.dialog.open(AdminWithdrawalTransactionCreateDialogComponent, {
      minWidth: '990px',
      maxWidth: '990px',
      maxHeight: 'calc(100vh - 96px)',
      position: {
        top: '48px',
      },
      panelClass: 'withdrawal-transaction-create-dialog',
      disableClose: true,
      data: {}
    });

    dialogRef.afterClosed().subscribe((newRow: WithdrawalTransaction) => {

      console.log('closed', newRow);

      // updated が返って来なければ何もしない（キャンセルなど）
      if (!newRow) return;

      // 再ロード
      this.snackBar.show(`単独の送金処理管理が追加されました\n\n user_id=${newRow.user_id}\n amount=${newRow.currency} ${newRow.requested_amount}\n created_at=${newRow.created_at}`, undefined, 5000);
      this.load();
    });

  }
}
